import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { postAdmin, postAdminDownload, saveBlob } from '../lib/adminApi.ts'
import { fmtNum } from '../lib/format.ts'
import { Banner, Pill } from '../ui/components.tsx'
import { type Sensitive } from '../ui/sensitive.tsx'

/** The pseudonym the server gives an erased person (private.pseudonym_of). A picture only: the server decides whether a person is erased. */
export const ERASED_NAME = /^Personne effacée [0-9A-F]{4}$/
export const isErased = (p: { display_name: string; employment_state?: string }) => ERASED_NAME.test(p.display_name) && p.employment_state !== 'active'

const KEPT = ['work', 'safety', 'machines', 'qualifications', 'pay', 'history'] as const
const ERASED = ['name', 'photo', 'contacts', 'pin', 'login', 'roles', 'position'] as const

const size = (bytes: number, lang: string) => (bytes >= 1_048_576 ? `${fmtNum(bytes / 1_048_576, lang)} Mo` : `${fmtNum(Math.max(bytes, 1) / 1024, lang)} ko`)

/** A person's data rights (GDPR; ARCHITECTURE 16.2; docs/runbooks/dsar.md and erasure.md), for the direction only, online and behind a fresh second-factor code:
 *  the access export (a ZIP the server builds and forgets; the export is recorded) and the erasure (the name, contacts, PIN, login and photo go; what the law keeps stays,
 *  under the pseudonym). Both are REQUESTS to the admin endpoint: the database decides who may, and refuses the last administrator and oneself. */
export function PersonPrivacy({ person, s, canExport, canErase, onErased }: {
  person: { id: string; display_name: string; employment_state?: string }; s: Sensitive; canExport: boolean; canErase: boolean; onErased: () => void
}) {
  const { t, i18n } = useTranslation()
  const { org, site, deviceId, syncSoon } = useClub()
  const [asking, setAsking] = useState(false)
  const [read, setRead] = useState(false)
  const [busy, setBusy] = useState(false)
  const commandId = useRef(newId())
  const erased = isErased(person)

  async function exportData() {
    setBusy(true)
    try {
      const out = await s.wrap(() => postAdminDownload(`/admin/people/${person.id}/dsar`, { org_id: org.org_id, device_id: deviceId }))
      if (!out) return
      saveBlob(out.blob, out.filename)
      s.setOk(t('people.privacy.exported', { name: out.filename, size: size(out.blob.size, i18n.language) }))
    } finally { setBusy(false) }
  }
  async function erase() {
    setBusy(true)
    try {
      const out = await s.wrap(() => postAdmin<{ pseudonym: string | null; repeat: boolean }>(`/admin/people/${person.id}/erase`, { org_id: org.org_id, site_id: site.site_id, device_id: deviceId, command_id: commandId.current }))
      if (!out) return
      commandId.current = newId(); setAsking(false); setRead(false)
      s.setOk(out.repeat ? t('people.privacy.alreadyErased') : t('people.privacy.erased', { pseudonym: out.pseudonym ?? '' }))
      syncSoon(); onErased()
    } finally { setBusy(false) }
  }

  return (
    <div className="flex flex-col gap-3 border-t pt-4" data-testid="person-privacy">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-bold">{t('people.privacy.title')}</h3>
        {erased && <Pill tone="gray">{t('people.privacy.erasedPill')}</Pill>}
      </div>
      {canExport && (
        <div className="flex flex-col gap-2">
          <p className="meta">{t('people.privacy.exportHelp')}</p>
          <button className="btn" disabled={s.busy || busy || !s.online} onClick={() => void exportData()} data-testid="privacy-export">{t('people.privacy.export')}</button>
        </div>
      )}
      {canErase && !erased && !asking && (
        <button className="btn btn-danger" disabled={s.busy || busy || !s.online} onClick={() => setAsking(true)} data-testid="privacy-erase">{t('people.privacy.erase')}</button>
      )}
      {canErase && !erased && asking && (
        <div className="card flex flex-col gap-3 border-2 border-red-400 dark:border-red-500" data-testid="privacy-erase-confirm">
          <h4 className="text-lg font-bold">{t('people.privacy.confirmTitle', { name: person.display_name })}</h4>
          <div>
            <p className="font-semibold">{t('people.privacy.erasedTitle')}</p>
            <ul className="list-disc pl-5 text-sm">{ERASED.map((k) => <li key={k}>{t(`people.privacy.what.${k}`)}</li>)}</ul>
          </div>
          <div data-testid="privacy-kept">
            <p className="font-semibold">{t('people.privacy.keptTitle')}</p>
            <ul className="list-disc pl-5 text-sm">{KEPT.map((k) => <li key={k}>{t(`people.privacy.kept.${k}`)}</li>)}</ul>
            <p className="meta">{t('people.privacy.keptWhy')}</p>
          </div>
          <Banner tone="warn">{t('people.privacy.before')}</Banner>
          <label className="flex min-h-11 items-center gap-3 text-sm font-semibold">
            <input type="checkbox" className="size-6" checked={read} onChange={(e) => setRead(e.target.checked)} data-testid="privacy-erase-read" />
            {t('people.privacy.read')}
          </label>
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-danger" disabled={!read || s.busy || busy || !s.online} onClick={() => void erase()} data-testid="privacy-erase-go">{t('people.privacy.eraseGo')}</button>
            <button className="btn" disabled={busy} onClick={() => { setAsking(false); setRead(false) }}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  )
}
